import React, { useState, useEffect } from 'react';
import './About.css';

const EmailIcon = () => (
  <span style={{fontSize: '1.4rem', fontWeight: 700, lineHeight: 1, fontFamily: 'system-ui, -apple-system, sans-serif'}} aria-hidden="true">@</span>
);

const WebsiteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V10H5.67v8.34h2.67zM7 8.84a1.55 1.55 0 1 0 0-3.1 1.55 1.55 0 0 0 0 3.1zm11.34 9.5v-4.6c0-2.47-1.34-3.62-3.13-3.62a2.7 2.7 0 0 0-2.45 1.35V10h-2.67v8.34h2.67v-4.65c0-1.23.23-2.42 1.75-2.42 1.5 0 1.52 1.4 1.52 2.5v4.57h2.31z"/>
  </svg>
);

const About = () => {
  const [teamData, setTeamData] = useState({});

  useEffect(() => {
    // Load only team data from JSON
    fetch('/content/team.json')
      .then(res => res.json())
      .then(data => setTeamData(data))
      .catch(error => {
        console.error('Error loading team data:', error);
      });
  }, []);

  return (
    <div className="about">
      {/* Hero Section */}
      <section
        className="about-hero"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/about.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 70%',
        }}
      >
        <div className="hero-overlay"></div>
        <div className="container">
          <h1 className="page-title">Who We Are</h1>
          <p className="hero-subtitle">
            Discover the European Brief Therapy Association - our mission, history, and the people behind our work
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision-section">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="vision-card">
              <h2>Vision</h2>
              <p>A flourishing international Solution Focused community where people, ideas, and associations connect to co-create meaningful change through collaboration, learning, and shared practice.</p>
            </div>
            <div className="mission-card">
              <h2>Mission</h2>
              <p>To enhance and grow the Solution Focused (SF) movement across Europe by:</p>
              <ul>
                <li>Strengthening networks that connect practitioners, associations, and countries.</li>
                <li>Supporting the recognition and validation of the SF approach through research and accreditation.</li>
                <li>Encouraging learning, innovation, and dialogue that help the SF approach evolve and thrive.</li>
              </ul>
            </div>
          </div>
          
          <div className="statutes-section">
            <h3>Our Statutes</h3>
            <div className="pdf-link-section">
              <a 
                href="/pdfs/EBTA_statutes.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="pdf-btn"
              >
                📄 Download EBTA Statutes (PDF)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* History */}
      <section className="history-section">
        <div className="container">
          <h2>Our History</h2>
          <div className="history-content">
            <p>
              Founded in 1993 by Steve de Shazer and Insoo Kim Berg and a group of European solution focused practitioners—Luc Isebaert, Wolfgang Eberling, Anders Claesson, Harvey Ratner, Marie-Christine Cabié and Manfred Vogt, EBTA provides a European platform for people interested in Solution Focused therapy.
            </p>
            <p>
              EBTA is an open, non-profit, civil society organization for people interested in supporting solution-focused 
              work in different contexts within the helping professions and communities in Europe in co-operation with other 
              approaches, practices and research methods. EBTA supports the development of solution-focused ideas, theory and practice.
            </p>
            <p>
              EBTA promotes dialogue with other solution-focused actors and other approaches and has a history since 1993 
              that has briefly been described in June 2011 by Dr. Luc Isebaert.
            </p>
            <div className="pdf-link-section">
              <a 
                href="/pdfs/EBTA_history.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="pdf-btn"
              >
                📄 Read More History (PDF)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Diversity & Inclusion */}
      <section className="diversity-section">
        <div className="container">
          <h2>Diversity and Inclusion</h2>
          <div className="diversity-content">
            {/* First row: Text left, Image right */}
            <div className="diversity-row">
              <div className="diversity-text-left">
                <h3>Understanding Diversity</h3>
                <p>
                  We believe that diversity and inclusion are two concepts that are closely related, while not interchangeable.
                </p>
                <p>
                  Diversity is about people and perspectives, the representation or composition of an entity, and is about the rainbow of human differences, including but not limited to race, ethnicity, gender, gender identity, sexual orientation, social class, age, physical abilities or characteristics, religious or ethical value system, national origin and political beliefs.
                </p>
                <p>
                  EBTA's goal is to treat all individuals and groups with respect, dignity and inclusion. Inclusion dwells on being aware of dealing with power, having a voice and a safe organizational culture where everyone is included.
                </p>
              </div>
              <div className="diversity-image-right">
                <img src="/images/diversity1.jpg" alt="Diverse group of professionals" />
              </div>
            </div>

            {/* Second row: Image left, Text right */}
            <div className="diversity-row reverse">
              <div className="diversity-image-left">
                <img src="/images/diversity2.jpg" alt="Inclusive workshop setting" />
              </div>
              <div className="diversity-text-right">
                <h3>Our Commitment to Inclusion</h3>
                <p>
                  Inclusion, in relation to our work, also means engagement and empowerment, recognizing the inherent and unique value and dignity of each person. EBTA, as a solution focused practice organization, aims to develop and promote the full value of both diversity and inclusiveness and to support people's sense of belonging.
                </p>
                <p>
                  EBTA aims to make this visible in its actions with full respect for the talents, beliefs, backgrounds and ways of life of people inside and outside its organization. All people have the right to be respected and appreciated as valuable members of the communities.
                </p>
                <p>
                  Both concepts, diversity and inclusion are closely related to the fundamental premises of the EBTA code of ethics and the specific guidelines for the SFBT practitioner.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Code of Ethics */}
      <section className="ethics-section">
        <div className="container">
          <h2>Code of Ethics</h2>
          <p>
            Our Code of Ethics guides all EBTA activities and member conduct. It reflects our commitment 
            to the highest standards of professional practice.
          </p>
          <div className="pdf-link-section">
            <a 
              href="/pdfs/EBTA_code_of_ethics.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="pdf-btn"
            >
              📄 View Full Code of Ethics (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <h2>Meet Our Team!</h2>
          
          {/* Board Members Section */}
          <div className="board-section">
            <h3>ACTUAL BOARD MEMBERS SECTION</h3>
            <p>EBTA board was elected during MAM in September 2025</p>
            <div className="team-grid">
              {teamData.boardMembers && teamData.boardMembers
                .filter(member => member.role) // Only show members with a role
                .map((member, index) => (
                <div key={index} className="team-member">
                  <div className="member-photo">
                    <img src={member.image} alt={member.name} />
                  </div>
                  <h3>{member.name}</h3>
                  <p className="member-role">{member.role}</p>
                  {member.taskGroups && member.taskGroups.length > 0 && (
                    <p className="member-taskgroups">{member.taskGroups.join(' & ')}</p>
                  )}
                  <div className="member-links">
                    {member.email && (
                      <a href={`mailto:${member.email}`} className="contact-link contact-link--email" aria-label={`Email ${member.name}`} title={`Email ${member.name}`}>
                        <EmailIcon />
                      </a>
                    )}
                    {member.website && (
                      <a href={member.website} target="_blank" rel="noopener noreferrer" className="contact-link contact-link--web" aria-label={`Website of ${member.name}`} title="Website">
                        <WebsiteIcon />
                      </a>
                    )}
                    {member.website2 && (
                      <a href={member.website2} target="_blank" rel="noopener noreferrer" className="contact-link contact-link--web" aria-label={`Second website of ${member.name}`} title="Website">
                        <WebsiteIcon />
                      </a>
                    )}
                    {member.facebook && (
                      <a href={member.facebook} target="_blank" rel="noopener noreferrer" className="contact-link contact-link--facebook" aria-label={`Facebook of ${member.name}`} title="Facebook">
                        <FacebookIcon />
                      </a>
                    )}
                    {member.linkedin && (
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="contact-link contact-link--linkedin" aria-label={`LinkedIn of ${member.name}`} title="LinkedIn">
                        <LinkedInIcon />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* View Detailed Team Profiles button (between Board and Honorary) */}
          <div className="team-details">
            <div className="pdf-link-section">
              <a
                href="/pdfs/EXTRA-TEAMINFO-2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-btn"
              >
                📄 View Detailed Team Profiles (PDF)
              </a>
            </div>
          </div>

          {/* Honorary Members Section */}
          <div className="honorary-section">
            <h3>HONORARY BOARD MEMBERS SECTION</h3>
            <p>Honorary board members position has been designed in 2024 to validate our history and to honor founding members of the EBTA.</p>
            <p>Honorary board members can join every board meeting with advice and wisdom (not to vote or decide)</p>
            <div className="team-grid">
              {teamData.honoraryMembers && teamData.honoraryMembers.map((member, index) => (
                <div key={index} className="team-member honorary">
                  <div className="member-photo">
                    <img src={member.image} alt={member.name} />
                  </div>
                  <h3>{member.name} ({member.country})</h3>
                  <p className="member-role">{member.role}</p>
                  <p className="member-description">{member.description}</p>
                  {member.taskGroups && member.taskGroups.length > 0 && (
                    <p className="member-taskgroups">{member.taskGroups.join(' & ')}</p>
                  )}
                  <div className="member-links">
                    {member.email && (
                      <a href={`mailto:${member.email}`} className="contact-link contact-link--email" aria-label={`Email ${member.name}`} title={`Email ${member.name}`}>
                        <EmailIcon />
                      </a>
                    )}
                    {member.website && (
                      <a href={member.website} target="_blank" rel="noopener noreferrer" className="contact-link contact-link--web" aria-label={`Website of ${member.name}`} title="Website">
                        <WebsiteIcon />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default About;
